import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { trackEvent } from '../../utils/tracking';
import { useSFX } from 'js/hooks/useSFX';

// import actions
import { toggleFavoriteCuratedCollection } from 'js/store/user/User.actions.js';

// import selectors
import { getTranslationById } from 'js/store/app/App.selectors';
import { getUserCollections } from 'js/store/collections/Collections.selectors';
import { getCardById, getCuratedCollections, getUserFavoritedCuratedCollections } from 'js/store/cards/Cards.selectors';

// import components
import Thumbnail from 'js/components/thumbnail/Thumbnail';
import SwiperVirtual from 'js/components/swiper/Swiper.virtual';

// import icons
import IconHeart from 'js/components/icon/Icon.heart';
import { getSkillTagTranslation } from '../../store/app/App.selectors';

const CollectionCarousel = (props) => {
  const defaultTitle = useSelector((state) =>
    getTranslationById(state, 'library_page_category_your_galleries', 'Your Galleries')
  );

  const { title = defaultTitle, type = 'user', filter = '' } = props;
  const data = useSelector((state) => {
    return type == 'user'
      ? getUserCollections(state)
      : filter == 'user-favorites'
      ? getUserFavoritedCuratedCollections(state)
      : getCuratedCollections(state);
  });

  const renderSlider = () => {
    const slides =
      !data || data?.length === 0
        ? [
            <CollectionPlaceholderSlide key="placeholder-1" type={type} />,
            <CollectionPlaceholderSlide key="placeholder-2" type={type} />,
            <CollectionPlaceholderSlide key="placeholder-3" type={type} />,
          ]
        : data?.map((collection) => {
            return (
              <CollectionSlide
                key={collection.id}
                cards={collection.cards}
                name={collection.name}
                collectionId={collection.id}
                type={type}
              />
            );
          });

    return <SwiperVirtual slides={slides} />;
  };

  return (
    <>
      <h2>{title}</h2>
      {renderSlider()}
    </>
  );
};

const CollectionSlide = ({ cards, name, collectionId, type }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { playSFX } = useSFX();

  const localisedCuratedName = useSelector((state) => getSkillTagTranslation(state, name));
  const localisedName = useSelector((state) => getTranslationById(state, name, name));

  let firstCard = useSelector((state) => getCardById(state, cards[0]));
  const { largeImage } = firstCard?.front || {};

  const favorited = useSelector((state) => {
    if (type === 'curated') {
      return state?.user?.favoriteCuratedCollections?.includes(collectionId) || false;
    }
    return false;
  });

  const addToFavorites = (e) => {
    playSFX('button_tap');
    playSFX('hearting_a_card_feedback');

    e.stopPropagation();
    e.preventDefault();
    trackEvent(favorited ? 'game_collection_unfavorite' : 'game_collection_favorite', {
      collection_id: collectionId,
    });
    dispatch(toggleFavoriteCuratedCollection(collectionId));
  };

  return (
    <div
      style={{ position: 'relative', cursor: 'pointer' }}
      onClick={() => {
        playSFX('button_tap');
        navigate('/collections/' + collectionId, {
          state: { displayOtherButtons: type === 'user' },
        });
      }}
    >
      {type === 'curated' && (
        <button
          className="card__icon card__icon--favorite card__icon--favorite-curated"
          onClick={addToFavorites}
          aria-label="Add to favorite curated collections"
        >
          <IconHeart active={favorited} />
        </button>
      )}
      <Thumbnail
        src={largeImage}
        alt={type === 'curated' ? localisedCuratedName : localisedName}
        caption={type === 'curated' ? localisedCuratedName : localisedName}
      />
    </div>
  );
};

const CollectionPlaceholderSlide = ({ type }) => {
  const siteText = {
    user: useSelector((state) =>
      getTranslationById(
        state,
        'default_text_your_saved_galleries_will_appear_here',
        'Your saved galleries will appear here.'
      )
    ),
    curated: useSelector((state) =>
      getTranslationById(
        state,
        'default_text_your_saved_curated_collections_will_appear_here',
        'Your saved curated collections will appear here.'
      )
    ),
  };
  const text = type == 'user' ? siteText.user : siteText.curated;
  return (
    <div className="placeholder-slide--collection">
      <button
        className="card__icon card__icon--favorite card__icon--favorite-curated"
        aria-label="Add to favorite curated collections"
      >
        <IconHeart active={false} />
      </button>
      <p>{text}</p>
    </div>
  );
};

export default CollectionCarousel;
